import { de } from './de'
import { en } from './en'
import { beschriftungen } from './beschriftungen'
import type { Sprachschluessel, Uebersetzung } from './de'
import type { Zahlendarstellung } from '../typen'

export type { Sprachschluessel, Uebersetzung }

/**
 * Mehrsprachigkeit von der ersten Zeile an (ADR 010, L11★, R157).
 *
 * Drei Schichten, in dieser Reihenfolge gefragt:
 *
 *   1. **Begriffe des Hauses** — was der Kunde für sein Haus gesetzt hat.
 *   2. **Die gewählte Sprache** — DE und EN ab Werk, weitere vom Kunden.
 *   3. **Deutsch** als Rückfall.
 *
 * Der Rückfall auf Deutsch ist bewusst und nicht der Rückfall auf den
 * Schlüssel: Ein Fachanwender, der `bereich.stammdaten.name` liest, sieht
 * technischen Text — genau das verbietet R145. Dass eine Sprachdatei
 * unvollständig sein könnte, fängt ohnehin der Übersetzer ab, nicht die
 * Laufzeit: `en.ts` ist gegen den Schlüsselvorrat von `de.ts` typisiert und
 * lässt sich mit einer fehlenden Zeile nicht übersetzen.
 */
const SPRACHDATEIEN: Record<string, Uebersetzung> = { de, en }

/** Was ausgeliefert wird. Der Kunde ergänzt hier seine eigene Datei. */
export const SPRACHEN = Object.keys(SPRACHDATEIEN)

const RUECKFALL = 'de'
const GEMERKT = 'coko.sprache'

let aktuell = ermittleStartsprache()

/**
 * Womit fängt der Anwender an?
 *
 * Erst das, was er zuletzt gewählt hat, dann die Sprache seines Browsers, dann
 * Deutsch. Die Auslandstochter, die ihre Daten selbst meldet, soll sich nicht
 * bei jedem Quartal durch eine deutsche Oberfläche klicken, um auf Englisch zu
 * stellen.
 */
function ermittleStartsprache(): string {
  const gemerkt = leseGemerkte()
  if (gemerkt) {
    return gemerkt
  }

  const vomBrowser = navigator.language.slice(0, 2).toLowerCase()
  return SPRACHDATEIEN[vomBrowser] ? vomBrowser : RUECKFALL
}

function leseGemerkte(): string | null {
  try {
    const wert = window.localStorage.getItem(GEMERKT)
    return wert && SPRACHDATEIEN[wert] ? wert : null
  } catch {
    // Ein Browser, der den Speicher sperrt, bekommt die Rückfallsprache —
    // das ist kein Fehler, über den jemand etwas erfahren müsste.
    return null
  }
}

export function aktuelleSprache(): string {
  return aktuell
}

export function setzeSprache(code: string): void {
  if (!SPRACHDATEIEN[code]) {
    return
  }

  aktuell = code
  document.documentElement.lang = code

  try {
    window.localStorage.setItem(GEMERKT, code)
  } catch {
    // Siehe oben: gemerkt wird dann eben nichts.
  }
}

/**
 * Der Text zu einem Schlüssel, mit eingesetzten Werten.
 *
 * **Eingesetzt wird nur Text** (R841 — Eine Anzahl steht wie ein Betrag):
 * Wer eine Zahl in einen Satz setzt, entscheidet an der Stelle, ob sie eine
 * Anzahl ist ({@link anzahlwort}, „1.342") oder eine Nummer (`String(…)`,
 * „Zeile 1342"). Bis zum 2026-09-30 nahm der Baukasten jede Zahl roh an.
 *
 * Platzhalter stehen als `{name}` im Text und nicht als Stellennummern: Eine
 * Übersetzung darf die Reihenfolge ändern — im Englischen steht die Zahl vor
 * dem Verb, im Deutschen dahinter —, und mit `{0}` und `{1}` wäre das eine
 * Fehlerquelle, die niemand beim Lesen sieht.
 */
export function t(
  schluessel: Sprachschluessel,
  werte?: Record<string, string>,
  /**
   * **Mit der Verweismarke** (7.1 Abschnitt 13, R1527): Ein Satz, der sagt,
   * was zu tun ist, trägt sein Ziel als `[[Wort]]`. Nur wer den Verweis auch
   * zeichnet — {@link Verweissatz} —, fragt mit Marke; überall sonst steht das
   * Wort als schlichter Text da.
   */
  mitVerweis = false,
): string {
  const haus = beschriftungen[aktuell]?.[schluessel]
  const text = haus ?? SPRACHDATEIEN[aktuell]?.[schluessel] ?? de[schluessel]
  const fertig = (s: string) => (mitVerweis ? s : ohneVerweismarke(s))

  if (!werte) {
    return fertig(ohneRegelvermerk(text))
  }

  // **Auch was eingesetzt wird, kommt ohne Regelnummer** (7.2 Abschnitt 34,
  // R1133): Der Grund eines Abbruchs ist oft ein Satz des Rechenkerns, und
  // der trägt seine Nummer — *„… betritt das System nicht (R488)."*
  return fertig(
    ohneRegelvermerk(
      text.replace(/\{(\w+)\}/g, (unveraendert, name: string) =>
        name in werte ? String(werte[name]) : unveraendert,
      ),
    ),
  )
}

/** Die Marke eines Verweises im Satz: `[[Konzernabschlüsse]]` (R1527). */
const VERWEISMARKE = /\[\[([^\]]+)\]\]/

/**
 * Der Satz ohne Marke — das Wort bleibt, die Klammern gehen.
 *
 * Fehlt der Text zu einem Schlüssel, bleibt es beim bisherigen Verhalten von
 * {@link t}; die Marke ändert daran nichts.
 */
export function ohneVerweismarke(text: string): string {
  return typeof text === 'string' && text.includes('[[') ? text.replace(new RegExp(VERWEISMARKE.source, 'g'), '$1') : text
}

/**
 * Der Satz, zerlegt um seinen Verweis: was davor steht, das Wort, was danach
 * steht. `null`, wenn der Satz keinen trägt.
 */
export function verweisteile(text: string): { vor: string; wort: string; nach: string } | null {
  const treffer = VERWEISMARKE.exec(text)
  if (!treffer) {
    return null
  }

  return {
    vor: text.slice(0, treffer.index),
    wort: treffer[1]!,
    nach: ohneVerweismarke(text.slice(treffer.index + treffer[0].length)),
  }
}

/**
 * Ein Satz ohne Regelnummer und ohne Kapitelverweis (7.2 Abschnitt 34, R1133).
 *
 * **Der Befund vom 2026-10-06:** Rund 90 Sätze, die der Anwender liest,
 * trugen eine Regelnummer — *„Aktualisieren Sie ihn dort selbst (R641)."* —
 * und 40 Zwecksätze der Stammdaten einen Verweis ins Fachkonzept —
 * *„(2.8 Abschnitt 17)"*. Beides beantwortet keine Frage des Anwenders.
 *
 * **Eine Stelle für alles:** Die meisten dieser Sätze schreibt der Rechenkern,
 * viele stehen schon gespeichert in Belegen und Läufen, und er braucht die
 * Nummern weiter — an ihnen erkennt er, welche Sätze eines Laufs in die
 * Rückmeldung gehören. Also bleibt der gespeicherte Text, und die Anzeige
 * liest ihn ohne.
 *
 * **Der Klartext hinter einer Nummer bleibt** — die Auflage zur Entscheidung,
 * dass auch die Prüferansicht keine Nummern zeigt: aus *„(R86 — eine Differenz
 * unter Toleranz wird gebucht)"* wird *„(eine Differenz unter Toleranz wird
 * gebucht)"*. Eine Klammer, in der danach nichts mehr steht, fällt ganz weg.
 */
export function ohneRegelvermerk(text: string): string {
  if (!/R\d|\d\.\d/.test(text)) {
    return text
  }

  return text
    .replace(/(\s*)\(([^()]*)\)/g, (ganz: string, vor: string, innen: string) => {
      if (NUR_KAPITELVERWEIS.test(innen)) {
        return ''
      }
      if (!REGELNUMMER.test(innen)) {
        return ganz
      }

      const rest = innen
        .replace(/\bR\d{1,4}[a-z]?\s*[—–]\s*/g, '')
        .replace(/\b(?:siehe|vgl\.)\s+(?=R\d)/gi, '')
        .replace(/\bR\d{1,4}[a-z]?\b/g, '')
        .replace(/\s*([,;])(?:\s*[,;])+/g, '$1')
        .replace(/^[\s,;:—–-]+|[\s,;:—–-]+$/g, '')
        .replace(/\s{2,}/g, ' ')

      return rest === '' ? '' : `${vor}(${rest})`
    })
    // Ohne Klammer am Satzende: *„… gar nicht — R740."*
    .replace(/\s*[—–]\s*R\d{1,4}[a-z]?(?=[.\s]*$)/g, '')
}

/** Eine Regelnummer, wie das Fachkonzept sie vergibt: R86, R1133. */
const REGELNUMMER = /\bR\d{1,4}[a-z]?\b/

/**
 * Eine Klammer, die nur auf das Fachkonzept verweist: *„(2.8)"*, *„(2.6,
 * 2.9)"*, *„(2.9 Abschnitt 17)"*, *„(5.1 Abschnitt 3.17)"*.
 */
const NUR_KAPITELVERWEIS =
  /^\s*\d{1,2}\.\d{1,2}(?:\s+Abschnitt\s+\d+(?:\.\d+)?[a-z]?)?(?:\s*(?:,|und)\s*\d{1,2}\.\d{1,2}(?:\s+Abschnitt\s+\d+(?:\.\d+)?[a-z]?)?)*\s*$/

/**
 * Die Ländereinstellung der gewählten Sprache — für Monatsnamen und als
 * Vorgabe für die Zifferngruppierung.
 *
 * Sie steht in der Sprachdatei und nicht im Code: Wer eine elfte Sprache
 * ergänzt, ergänzt damit auch die Zifferngruppierung, ohne eine Zeile
 * Programm anzufassen.
 */
export function gebietsschema(): string {
  return t('sprache.gebietsschema')
}

/**
 * Das Zahlenformat — **unabhängig von der Sprache wählbar.**
 *
 * Der Fall, der das verlangt: Wer eine Kundendatenbank betreut und seinen
 * Bildschirm abwechselnd deutschen und englischen Anwendern zeigt, will die
 * Schreibweise der Beträge umstellen können, ohne die Oberfläche mit
 * umzustellen — und umgekehrt. Sprache und Zifferngruppierung sind zwei
 * Fragen, die nur meistens dieselbe Antwort haben.
 *
 * **Fester Vorrat, freie Wahl** (R146): fünf Formate, keine frei wählbaren
 * Trennzeichen. Sonst ließe sich Punkt als Dezimal- *und* als
 * Tausendertrennzeichen einstellen, und die Zahl wäre nicht mehr lesbar.
 *
 * Die zwei Nachkommastellen sind nicht Teil der Wahl: Ein Konzernabschluss
 * rechnet in Cent, und eine Spalte, in der die Stellenzahl wechselt, ist nicht
 * mehr zu vergleichen.
 */
export type Zahlenformat = 'sprache' | 'de' | 'en' | 'fr' | 'ch'

const ZAHLENFORMATE: Record<Exclude<Zahlenformat, 'sprache'>, string> = {
  de: 'de-DE',
  en: 'en-GB',
  fr: 'fr-FR',
  ch: 'de-CH',
}

export const ZAHLENFORMATWAHL: Zahlenformat[] = ['sprache', 'de', 'en', 'fr', 'ch']

const FORMAT_GEMERKT = 'coko.zahlenformat'

let zahlenwahl: Zahlenformat = leseZahlenformat()

function leseZahlenformat(): Zahlenformat {
  try {
    const wert = window.localStorage.getItem(FORMAT_GEMERKT)
    return wert && ZAHLENFORMATWAHL.includes(wert as Zahlenformat)
      ? (wert as Zahlenformat)
      : 'sprache'
  } catch {
    return 'sprache'
  }
}

export function aktuellesZahlenformat(): Zahlenformat {
  return zahlenwahl
}

export function setzeZahlenformat(wahl: Zahlenformat): void {
  zahlenwahl = wahl

  try {
    window.localStorage.setItem(FORMAT_GEMERKT, wahl)
  } catch {
    // Gemerkt wird dann nichts; die Wahl gilt für diese Sitzung.
  }
}

/** Die Ländereinstellung, nach der Beträge gruppiert werden. */
export function zahlenschema(): string {
  return zahlenwahl === 'sprache' ? gebietsschema() : ZAHLENFORMATE[zahlenwahl]
}

/**
 * Das Dezimalzeichen der eingestellten Schreibweise.
 *
 * Die Suche braucht es für den einen wirklich mehrdeutigen Fall — „1.100" ist
 * je nach Einstellung tausendeinhundert oder eins Komma eins.
 */
export function dezimaltrenner(): string {
  const teil = new Intl.NumberFormat(zahlenschema())
    .formatToParts(1.1)
    .find((t) => t.type === 'decimal')

  return teil?.value ?? ','
}

/**
 * Ein Beispiel, wie das Format aussieht.
 *
 * In der Auswahlliste steht die Zahl selbst und nicht ihr Name: „1.234.567,89"
 * erklärt sich, „deutsche Schreibweise" muss man erst zuordnen.
 */
export function zahlenbeispiel(wahl: Zahlenformat): string {
  const schema = wahl === 'sprache' ? gebietsschema() : ZAHLENFORMATE[wahl]
  return (1234567.89).toLocaleString(schema, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Der Zustand eines Abschlusses, wie ihn die API liefert (2.13).
 *
 * Sie schickt einen Schlüssel — `offen`, `in_arbeit`, `freigegeben`,
 * `testiert` —, keinen Satz. Genau richtig: Ein Zustand ist eine Bedeutung,
 * seine Beschriftung gehört auf diese Seite. Ein unbekannter Wert wird
 * unverändert durchgereicht: Er zeigt an, dass die API einen Zustand kennt,
 * den die Oberfläche noch nicht beschriftet — sichtbar, aber nicht kaputt.
 */
const ABSCHLUSSSTATUS: Record<string, Sprachschluessel> = {
  offen: 'status.offen',
  in_arbeit: 'status.in_arbeit',
  freigegeben: 'status.freigegeben',
  testiert: 'status.testiert',
}

export function abschlussstatus(wert: string): string {
  const schluessel = ABSCHLUSSSTATUS[wert]
  return schluessel ? t(schluessel) : wert
}

/* ==========================================================================
   Zahlen, Perioden und die Sätze des Rechenkerns
   ==========================================================================

   Diese drei stehen hier und nicht bei der Schnittstelle: Sie hängen
   ausschließlich an der Sprache und am gewählten Zahlenformat. Wer sie dort
   ließe, wo die Daten herkommen, hätte zwei Orte, an denen entschieden wird,
   wie eine Zahl aussieht. */

/**
 * Beträge in der Schreibweise, die der Anwender gewählt hat.
 *
 * Zwei Nachkommastellen sind nicht Teil der Wahl: Ein Konzernabschluss rechnet
 * in Cent, und eine Spalte, in der die Stellenzahl wechselt, ist nicht mehr zu
 * vergleichen.
 */
export function betrag(wert: number): string {
  return wert.toLocaleString(zahlenschema(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Eine **Anzahl** im Zahlenformat des Anwenders — „1.342", „23.651" (7.2
 * Abschnitt 23, R841 — Eine Anzahl steht wie ein Betrag).
 *
 * **Nur für das, was zählt:** Einträge, Zeilen, Aufrufe, Millisekunden. Eine
 * Nummer — Jahr, Periode, Konto-, Beleg- oder Zeilennummer — benennt und
 * trägt keinen Tausendertrenner; sie geht als `String(…)` in den Satz.
 */
export function anzahlwort(wert: number): string {
  return wert.toLocaleString(zahlenschema(), { maximumFractionDigits: 0 })
}

/**
 * Kurse in der Schreibweise des Anwenders — mit **zehn** Nachkommastellen
 * (R43).
 *
 * **Warum ein zweiter Formatierer neben `betrag`.** Ein Betrag hat zwei
 * Nachkommastellen, ein Kurs zehn: Währungen mit großen Nominalwerten — JPY,
 * IDR, VND — schneiden bei vier Stellen in der vierten signifikanten Ziffer
 * ab, und dann rechnet ein Abschluss mit einer Zahl, die auf dem Bildschirm
 * anders steht.
 *
 * **Angezeigt werden vier bis zehn Stellen.** Vier, weil 0,92 als „0,9200"
 * lesbarer in einer Spalte steht als „0,92"; zehn, weil mehr nicht gespeichert
 * wird. Nachgestellte Nullen dazwischen fallen weg — sonst stünde neben jedem
 * gepflegten Kurs eine Reihe Nullen, die nichts sagt.
 */
export function kurswert(wert: number): string {
  return wert.toLocaleString(zahlenschema(), {
    minimumFractionDigits: 4,
    maximumFractionDigits: 10,
  })
}

/* --------------------------------------------------------------------------
   Kommazahlen der Stammdaten (7.2 Abschnitt 22, R840 — Eine Zahl in den
   Stammdaten steht wie im Bericht; Entscheidung des Auftraggebers vom
   2026-09-30)
   --------------------------------------------------------------------------

   Befund aus der Durchsicht vom 2026-09-29: Im Beteiligungsregister stand der
   Kapitalanteil als „0.80000000" und die Anschaffungskosten als „1600000.00"
   — die Speicherform, mit Punkt und allen Endnullen der Spalte —, während die
   Fußleiste desselben Bildschirms „1.234.567,89" vorgibt. Und derselbe Anteil
   stand an fünf Stellen in fünf Formen.

   **Gelesen und geschrieben wird am Text, nicht über Fließkomma.** Ein Anteil
   ist als Bruch gespeichert und steht als Prozent da; 0,07 mal 100 ergibt im
   Fließkomma 7,000000000000001. Deshalb wird das Komma an den Ziffern
   verschoben und an den Ziffern gerundet. */

/** Eine Zahl, zerlegt in Vorzeichen, Ziffern vor und Ziffern nach dem Komma. */
type Ziffern = { negativ: boolean; ganz: string; nach: string }

/** So kommt eine Zahl vom Server: Ziffern, höchstens ein Punkt, kein Tausendertrenner. */
const SPEICHERFORM = /^([+-])?(\d*)(?:\.(\d*))?$/

/**
 * Höchstens sechs Nachkommastellen am Prozentwert — mehr trägt der gespeicherte
 * Bruch nicht (2.2, R13: acht Stellen am Bruch sind sechs am Prozent). Die
 * Anzeige rundet damit nie; sie lässt nur Endnullen weg.
 */
const ANTEILSTELLEN = 6

/** Höchstens zehn Nachkommastellen an einer Kommazahl — so viele trägt ein Kurs (R43). */
const DEZIMALSTELLEN = 10

/** Was der Rechenkern in Prozent liefert — 80, nicht 0,8 (R840, 22.3). */
const PROZENTFELDER = new Set(['quote', 'herleitung.quote', 'herleitung.fremdquote'])

function leer(wert: number | string | null | undefined): boolean {
  return wert === null || wert === undefined || (typeof wert === 'string' && wert.trim() === '')
}

function bereinigt(z: Ziffern): Ziffern {
  const ganz = z.ganz.replace(/^0+(?=\d)/, '') || '0'
  const nach = z.nach.replace(/0+$/, '')

  // Minus null gibt es nicht: Was auf null gerundet ist, trägt kein Vorzeichen.
  return { negativ: z.negativ && (ganz !== '0' || nach !== ''), ganz, nach }
}

/** Die Speicherform oder eine Zahl in ihre Ziffern zerlegt — `null`, wenn es keine Zahl ist. */
function zerlege(wert: number | string | null | undefined): Ziffern | null {
  if (wert === null || wert === undefined) {
    return null
  }

  let text: string
  if (typeof wert === 'number') {
    if (!Number.isFinite(wert)) {
      return null
    }

    // Eine Fließkommazahl trägt fünfzehn verlässliche Ziffern. Was dahinter
    // steht, ist Rauschen — 0,1 + 0,2 wäre sonst 0,30000000000000004.
    const vorkomma = Math.abs(wert) < 1 ? 0 : Math.trunc(Math.abs(wert)).toString().length
    text = wert.toFixed(Math.max(0, Math.min(DEZIMALSTELLEN, 15 - vorkomma)))
  } else {
    text = wert.trim()
  }

  const teile = SPEICHERFORM.exec(text)
  if (!teile) {
    return null
  }

  const ganz = teile[2] ?? ''
  const nach = teile[3] ?? ''

  return ganz === '' && nach === '' ? null : bereinigt({ negativ: teile[1] === '-', ganz, nach })
}

/** Das Komma um so viele Stellen nach rechts (positiv) oder links (negativ) — exakt. */
function verschiebe(z: Ziffern, stellen: number): Ziffern {
  if (stellen >= 0) {
    const nach = z.nach.padEnd(stellen, '0')
    return bereinigt({
      negativ: z.negativ,
      ganz: z.ganz + nach.slice(0, stellen),
      nach: nach.slice(stellen),
    })
  }

  const ganz = z.ganz.padStart(1 - stellen, '0')
  return bereinigt({
    negativ: z.negativ,
    ganz: ganz.slice(0, stellen),
    nach: ganz.slice(stellen) + z.nach,
  })
}

/** Kaufmännisch gerundet auf so viele Nachkommastellen — an den Ziffern. */
function gerundet(z: Ziffern, stellen: number): Ziffern {
  if (z.nach.length <= stellen) {
    return z
  }

  const ziffern = (z.ganz + z.nach.slice(0, stellen)).split('')

  if (z.nach.charAt(stellen) >= '5') {
    let i = ziffern.length - 1
    while (i >= 0 && ziffern[i] === '9') {
      ziffern[i] = '0'
      i--
    }

    if (i >= 0) {
      ziffern[i] = String(Number(ziffern[i]) + 1)
    } else {
      ziffern.unshift('1')
    }
  }

  const alle = ziffern.join('')
  const schnitt = alle.length - stellen

  return bereinigt({ negativ: z.negativ, ganz: alle.slice(0, schnitt), nach: alle.slice(schnitt) })
}

/** Die Ziffern als Speicherform — Punkt, keine Endnullen: `0.8`, `1600000`, `-12.5`. */
function speichertext(z: Ziffern): string {
  return (z.negativ ? '-' : '') + z.ganz + (z.nach ? `.${z.nach}` : '')
}

/** Ein Zeichen der eingestellten Schreibweise — Tausendertrenner, Komma, Minus. */
function zahlzeichen(art: 'group' | 'decimal' | 'minusSign', sonst: string): string {
  const teil = new Intl.NumberFormat(zahlenschema())
    .formatToParts(-1234567.5)
    .find((t) => t.type === art)

  return teil?.value ?? sonst
}

/** Die Ziffern im Zahlenformat des Anwenders — gruppiert wie bei {@link betrag}. */
function schreibe(z: Ziffern): string {
  // Die Vorkommastellen setzt derselbe Formatierer wie beim Betrag: So
  // gruppieren beide gleich, in jeder der Schreibweisen. Ganze Zahlen bis
  // fünfzehn Stellen sind im Fließkomma exakt; was länger ist, wird an den
  // Ziffern gruppiert.
  const ganz =
    z.ganz.length <= 15
      ? Number(z.ganz).toLocaleString(zahlenschema(), { maximumFractionDigits: 0 })
      : z.ganz.replace(/\B(?=(\d{3})+(?!\d))/g, zahlzeichen('group', ''))

  return (
    (z.negativ ? zahlzeichen('minusSign', '-') : '') +
    ganz +
    (z.nach ? zahlzeichen('decimal', ',') + z.nach : '')
  )
}

/**
 * Eine Zahl in der **Speicherform ohne Endnullen** — `'0.80000000'` → `'0.8'`,
 * `'1600000.00'` → `'1600000'`. Leer oder keine Zahl → `null`.
 *
 * **Wozu:** Der Server liefert einen Anteil mit den acht Stellen der Spalte,
 * das Eingabefeld meldet ihn ohne. Wer beide vergleicht — *hat sich etwas
 * geändert?* —, vergleicht über diese Form und nicht über den rohen Text.
 */
export function speicherform(roh: string | number | null | undefined): string | null {
  const z = zerlege(roh)
  return z ? speichertext(z) : null
}

/**
 * Eine Zahl in Prozent, **ohne Zeichen und ohne Endnullen** — „80", „33,3333".
 *
 * **Für Werte, die schon Prozent sind:** Der Rechenkern liefert den Anteil
 * einer Buchung als 80 und nicht als 0,8 — so steht er im Prüfcode des Laufs,
 * und was der Rechenkern gespeichert hat, bleibt (R840, 22.3). Der Satz sagt
 * selbst „{quote} Prozent"; die Zahl davor kommt von hier. Was keine Zahl ist,
 * bleibt stehen.
 */
export function prozentzahl(wert: number | string): string {
  const z = zerlege(wert)
  return z ? schreibe(gerundet(z, ANTEILSTELLEN)) : String(wert)
}

/** Dieselbe Prozentzahl **mit** Zeichen — „80 %" —, für die Herleitung einer Buchung. */
export function prozentangabe(wert: number | string): string {
  const z = zerlege(wert)
  return z ? `${schreibe(gerundet(z, ANTEILSTELLEN))} %` : String(wert)
}

/** Ob ein Wert des Rechenkerns ein Anteil in Prozent ist — am Feldnamen erkannt (R840, 22.3). */
export function istProzentfeld(feld: string): boolean {
  return PROZENTFELDER.has(feld)
}

/**
 * Was der Rechenkern als Anzahl liefert, ohne es so zu nennen (R841, 7.2
 * Abschnitt 23): Monate der Fortschreibung als `zahl` — über `betrag` stand
 * dort „12,00 Monate" —, gezählte Gesellschaften und Anhangsangaben als
 * `text`. Was er gespeichert hat, bleibt; die Anzeige setzt die Anzahl.
 */
const ANZAHLFELDER = new Set(['anzahl', 'monate', 'herleitung.monate', 'herleitung.nutzungsdauer'])

/** Ob ein Wert des Rechenkerns eine Anzahl ist — am Feldnamen erkannt (R841). */
export function istAnzahlfeld(feld: string): boolean {
  return ANZAHLFELDER.has(feld)
}

/**
 * Ein Anteil — der gespeicherte Bruch als Prozent: 0,8 → „80 %",
 * `'0.333333'` → „33,3333 %" (R840, Entscheidung 1 vom 2026-09-30).
 *
 * **So viele Nachkommastellen wie nötig, höchstens sechs.** Immer zwei Stellen
 * hieße, ein Drittel als „33,33 %" zu zeigen — das wäre falsch gezeigt. Der
 * Preis ist benannt: keine feste Spaltenbreite.
 *
 * **Überall dieselbe Form:** Register, Fenster der Kapitalkonsolidierung,
 * Prüferansicht. Das Leerzeichen vor dem Zeichen ist geschützt, damit Zahl und
 * Zeichen nicht auf zwei Zeilen fallen. Leer bleibt leer; was keine Zahl ist,
 * bleibt stehen.
 */
export function anteil(wert: number | string | null | undefined): string {
  if (leer(wert)) {
    return ''
  }

  const z = zerlege(wert)
  return z ? `${schreibe(gerundet(verschiebe(z, 2), ANTEILSTELLEN))} %` : String(wert)
}

/**
 * Eine Kommazahl im Zahlenformat des Anwenders, **ohne die Endnullen der
 * Spalte** — `'1600000.00'` → „1.600.000", `'1.0000000000'` → „1",
 * `'0.125'` → „0,125" (R840). Höchstens zehn Nachkommastellen.
 */
export function dezimalzahl(wert: number | string | null | undefined): string {
  if (leer(wert)) {
    return ''
  }

  const z = zerlege(wert)
  return z ? schreibe(gerundet(z, DEZIMALSTELLEN)) : String(wert)
}

/**
 * Eine Zahl der Stammdaten, wie sie in der Zelle steht (R840, 22.3).
 *
 * **Was sie ist, sagt die Darstellung an der Feldbeschreibung** — nicht der
 * Feldname: ein Anteil „80 %", ein Betrag „1.600.000,00", ein Kurs „1,0000".
 * Ohne Darstellung steht die Zahl im Zahlenformat des Anwenders, ohne die
 * Endnullen der Spalte.
 */
export function zahlenwort(
  roh: string | number | null | undefined,
  darstellung?: Zahlendarstellung | null,
): string {
  if (leer(roh)) {
    return ''
  }

  const z = zerlege(roh)
  if (!z) {
    return String(roh)
  }

  switch (darstellung) {
    case 'anteil':
      return anteil(roh)
    case 'betrag':
      return betrag(Number(speichertext(z)))
    case 'kurs':
      return kurswert(Number(speichertext(z)))
    default:
      return dezimalzahl(roh)
  }
}

/** Gruppiert nach deutscher Art: Punkte zwischen Dreiergruppen, Komma vor den Nachkommastellen. */
const GRUPPIERT_PUNKT = /^([1-9]\d{0,2}(?:\.\d{3})+)(?:,(\d*))?$/

/** Gruppiert nach englischer Art: Kommas zwischen Dreiergruppen, Punkt vor den Nachkommastellen. */
const GRUPPIERT_KOMMA = /^([1-9]\d{0,2}(?:,\d{3})+)(?:\.(\d*))?$/

/** Ohne Tausendertrenner: Dann ist ein einzelnes Komma oder ein einzelner Punkt das Dezimalzeichen. */
const UNGRUPPIERT = /^(\d*)(?:[.,](\d*))?$/

/**
 * Was der Anwender getippt hat, als Speicherform — „1.600.000,00" →
 * `'1600000'`, „0,8" → `'0.8'`, „-12,5" → `'-12.5'` (R840, Entscheidung 3).
 * Unlesbar oder leer → `null`.
 *
 * **Gelesen wird nach dem gewählten Zahlenformat.** „1.600" ist in deutscher
 * Schreibweise tausendsechshundert und in englischer eins Komma sechs — das
 * ist der eine wirklich mehrdeutige Fall, und ihn entscheidet die Fußleiste.
 *
 * **Und großzügig, wo nichts mehrdeutig ist:** Steht kein Tausendertrenner
 * da, gelten Komma und Punkt beide als Dezimalzeichen — „1600000.5" ist in
 * jeder Schreibweise dieselbe Zahl. Ein Punkt zählt nur dann als
 * Tausendertrenner, wenn Dreiergruppen folgen und die Zahl nicht mit null
 * beginnt: „0.125" bleibt ein Achtel. Leerzeichen und Apostroph sind in
 * keiner Schreibweise ein Dezimalzeichen und fallen weg.
 *
 * **Ohne Umweg über Fließkomma** — die Ziffern bleiben, wie sie getippt
 * wurden.
 */
export function liesDezimal(text: string): string | null {
  let rest = text.replace(/[\s'’]/g, '')

  const vorzeichen = rest.charAt(0)
  const negativ = vorzeichen === '-' || vorzeichen === '−'
  if (negativ || vorzeichen === '+') {
    rest = rest.slice(1)
  }

  if (!/\d/.test(rest)) {
    return null
  }

  const trenner = zahlzeichen('group', '')
  const muster = trenner === '.' ? GRUPPIERT_PUNKT : trenner === ',' ? GRUPPIERT_KOMMA : null
  const gruppiert = muster?.exec(rest)

  if (gruppiert) {
    return speichertext(
      bereinigt({
        negativ,
        ganz: (gruppiert[1] ?? '').replace(/[.,]/g, ''),
        nach: gruppiert[2] ?? '',
      }),
    )
  }

  const schlicht = UNGRUPPIERT.exec(rest)
  return schlicht
    ? speichertext(bereinigt({ negativ, ganz: schlicht[1] ?? '', nach: schlicht[2] ?? '' }))
    : null
}

/**
 * Ein getippter Anteil als gespeicherter Bruch — „80" → `'0.8'`, „80,5" →
 * `'0.805'`, „33,3333 %" → `'0.333333'` (R840, Entscheidung 2).
 *
 * **Getippt wird in Prozent.** Wer aus Gewohnheit „0,8" tippt, bekommt
 * 0,8 % — das ist der Preis der Entscheidung, und er ist sichtbar: Das Feld
 * trägt das Prozentzeichen dahinter.
 *
 * Außerhalb von 0 bis 100 oder unlesbar → `null`. Gerechnet wird per
 * Stellenverschiebung, nicht per Division — „7" wird `'0.07'` und nichts
 * daneben.
 */
export function liesAnteil(text: string): string | null {
  const gelesen = liesDezimal(text.trim().replace(/%$/, ''))
  const z = zerlege(gelesen)
  if (!z || z.negativ) {
    return null
  }

  const prozent = gerundet(z, ANTEILSTELLEN)
  const ganz = prozent.ganz.length > 3 ? Number.POSITIVE_INFINITY : Number(prozent.ganz)
  if (ganz > 100 || (ganz === 100 && prozent.nach !== '')) {
    return null
  }

  return speichertext(verschiebe(prozent, -2))
}

/**
 * Der Wert, wie er **im Eingabefeld** steht (R840): ein Anteil als „80" —
 * das Prozentzeichen setzt das Feld dahinter —, ein Betrag als
 * „1.600.000,00", ein Kurs als „1,0000", jede andere Zahl ohne Endnullen.
 *
 * {@link liesAnteil} und {@link liesDezimal} lesen genau das wieder: Ein Feld,
 * das seinen eigenen Inhalt nicht annähme, wäre eines, das sich beim Verlassen
 * selbst für falsch erklärt.
 */
export function alsEingabe(
  roh: string | number | null | undefined,
  darstellung?: Zahlendarstellung | null,
): string {
  if (leer(roh)) {
    return ''
  }

  const z = zerlege(roh)
  if (!z) {
    return String(roh)
  }

  switch (darstellung) {
    case 'anteil':
      return schreibe(gerundet(verschiebe(z, 2), ANTEILSTELLEN))
    case 'betrag':
      return betrag(Number(speichertext(z)))
    case 'kurs':
      return kurswert(Number(speichertext(z)))
    default:
      return schreibe(gerundet(z, DEZIMALSTELLEN))
  }
}

/**
 * Der Satz zu einem Vorschlag der Beteiligungsart (R616) — mit den Stimmen im
 * Zahlenformat des Anwenders (R840).
 *
 * Der Server liefert die Stimmen als Prozentzahl in der Speicherform —
 * „33.3333" —, weil er das Zahlenformat nicht kennt (R157). Eingesetzt wurde
 * sie bis zum 2026-09-30 unverändert: „… mit 33.3333 % die Stimmenmehrheit".
 * Ein Grund, den die Sprachdatei nicht kennt, bleibt stehen, wie er kam.
 */
export function vorschlagssatz(
  grund: string,
  werte: Record<string, string> | null | undefined,
): string {
  const schluessel = `stammdaten.vorschlagsgrund.${grund}`
  if (!Object.hasOwn(de, schluessel)) {
    return grund
  }

  const gesetzt: Record<string, string> = { ...(werte ?? {}) }
  if (gesetzt.stimmen !== undefined) {
    gesetzt.stimmen = prozentzahl(gesetzt.stimmen)
  }

  return t(schluessel as Sprachschluessel, gesetzt)
}


/**
 * Wie ein Buchungsbetrag dasteht: mit Vorzeichen oder in zwei Spalten
 * (Auftrag vom 2026-09-27).
 *
 * **„Wir arbeiten mit Buchhaltern. Da ist das sehr wichtig."** Ein Betrag mit
 * Minuszeichen ist eine Rechengröße; Soll und Haben sind die Sprache der
 * doppelten Buchführung. Beides zeigt dieselbe Zahl, und wer täglich bucht,
 * liest die zweite Form schneller.
 *
 * **Soll-positiv ist die Ordnung des Hauses** — im Journal wie im Rechenkern.
 * Ein positiver Betrag steht deshalb im Soll, ein negativer im Haben, und
 * dort ohne sein Minuszeichen: In der Haben-Spalte ist es keine Auskunft
 * mehr, sondern eine Verdopplung.
 */
export type Vorzeichenart = 'plusminus' | 'sollhaben'

export const VORZEICHENWAHL: Vorzeichenart[] = ['plusminus', 'sollhaben']

const VORZEICHEN_GEMERKT = 'coko.vorzeichen'

let vorzeichenwahl: Vorzeichenart = leseVorzeichenart()

function leseVorzeichenart(): Vorzeichenart {
  try {
    const wert = window.localStorage.getItem(VORZEICHEN_GEMERKT)
    return wert && VORZEICHENWAHL.includes(wert as Vorzeichenart)
      ? (wert as Vorzeichenart)
      : 'plusminus'
  } catch {
    return 'plusminus'
  }
}

export function aktuelleVorzeichenart(): Vorzeichenart {
  return vorzeichenwahl
}

export function setzeVorzeichenart(wahl: Vorzeichenart): void {
  vorzeichenwahl = wahl

  try {
    window.localStorage.setItem(VORZEICHEN_GEMERKT, wahl)
  } catch {
    // Gemerkt wird dann nichts; die Wahl gilt für diese Sitzung.
  }
}

/**
 * In welcher Farbe ein negativer Betrag steht: rot wie im Standard oder
 * schwarz wie jeder andere (7.2 Abschnitt 32, R1015 — Auftrag vom 2026-10-04:
 * *„Reine Geschmackssache."*).
 *
 * **Gemerkt wie die Vorzeichen**, und wirksam über ein Kennzeichen an der
 * Wurzel der Seite: Jede Zahl, die ihr Minus rot zeigt, tut das über die eine
 * Farbe `--minus`, und die stellt das Stilblatt für `schwarz` auf die Farbe
 * der Schrift um. Kein Bauteil muss davon wissen.
 */
export type Minusfarbe = 'rot' | 'schwarz'

export const MINUSFARBWAHL: Minusfarbe[] = ['rot', 'schwarz']

const MINUSFARBE_GEMERKT = 'coko.minusfarbe'

let minusfarbwahl: Minusfarbe = leseMinusfarbe()
zeigeMinusfarbe(minusfarbwahl)

function leseMinusfarbe(): Minusfarbe {
  try {
    const wert = window.localStorage.getItem(MINUSFARBE_GEMERKT)
    return wert && MINUSFARBWAHL.includes(wert as Minusfarbe) ? (wert as Minusfarbe) : 'rot'
  } catch {
    return 'rot'
  }
}

function zeigeMinusfarbe(wahl: Minusfarbe): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.minusfarbe = wahl
  }
}

export function aktuelleMinusfarbe(): Minusfarbe {
  return minusfarbwahl
}

export function setzeMinusfarbe(wahl: Minusfarbe): void {
  minusfarbwahl = wahl
  zeigeMinusfarbe(wahl)

  try {
    window.localStorage.setItem(MINUSFARBE_GEMERKT, wahl)
  } catch {
    // Gemerkt wird dann nichts; die Wahl gilt für diese Sitzung.
  }
}

/**
 * Ein Betrag als Soll und Haben — die eine Seite trägt ihn, die andere ist
 * leer.
 *
 * **Die Null steht im Soll**, damit sie nicht verschwindet: Eine Buchung über
 * null ist selten und dann eine Auskunft.
 */
export function sollHaben(wert: number): { soll: string; haben: string } {
  return wert < 0
    ? { soll: '', haben: betrag(-wert) }
    : { soll: betrag(wert), haben: '' }
}

/**
 * **Der Anfang der Zeitachse** — die Periode, mit der „schon immer"
 * gespeichert ist. Gelesen wird sie als „Anfang", nie als „Januar 1900"
 * (7.4 Abschnitt 34.11, R1461).
 */
export const ANFANG = 190001

/** 202512 → „Dezember 2025" bzw. „December 2025". */
export function periodenname(periode: number): string {
  return periodenschrift(periode, 'long')
}

/**
 * Dieselbe Periode, **kurz** — 202512 → „Dez. 2025" bzw. „Dec 2025".
 *
 * **Für Spaltenköpfe und nur für sie** (Auftrag vom 2026-09-26): In der
 * Periodenansicht eines Berichts stehen bis zu zwölf Spalten nebeneinander,
 * und „September 2026" ist dort fast so breit wie die Zahl darunter. Im Satz
 * und in der Zeile einer Liste bleibt der Monat ausgeschrieben — dort kostet
 * er nichts und liest sich besser.
 *
 * **Das Jahr bleibt stehen.** Ein Bericht über zwei Geschäftsjahre hätte
 * sonst zwei Spalten „Jan.", und welche die des Vorjahrs ist, sagte nur die
 * Reihenfolge.
 */
export function periodenkuerzel(periode: number): string {
  return periodenschrift(periode, 'short')
}

function periodenschrift(periode: number, monatsform: 'long' | 'short'): string {
  const jahr = Math.floor(periode / 100)
  const monat = periode % 100

  // Solange die Auswahl nicht geladen ist, gibt es keine Periode. „0 0" wäre
  // eine Zahl, die nichts bedeutet — und die Arbeitsliste lebt davon, dass
  // jede Zahl darin etwas bedeutet.
  if (jahr === 0 || monat < 1 || monat > 12) {
    return t('kontext.periode.keine')
  }

  // „schon immer" ist kein Januar 1900 (R1461) — für jede Anzeige an dieser einen Stelle.
  if (periode <= ANFANG) {
    return t('periode.anfang')
  }

  // Der 15. und nicht der 1.: Ein Tag in der Mitte des Monats kann durch keine
  // Zeitzonenverschiebung in den Vormonat rutschen.
  return new Intl.DateTimeFormat(gebietsschema(), {
    month: monatsform,
    year: 'numeric',
  }).format(new Date(Date.UTC(jahr, monat - 1, 15)))
}

/**
 * Ein Zeitstempel als **Tagesdatum** — für Listen.
 *
 * **Der Tag reicht, die Uhrzeit nicht.** In einer Belegliste beantwortet
 * „12.09.2026" die Frage *wann zuletzt daran gearbeitet wurde*; die Sekunde
 * dazu kostet Spaltenbreite und sagt nichts, was auf dieser Flughöhe jemand
 * wissen will. Wer sie braucht, findet sie im Änderungsprotokoll — dort steht
 * sie vollständig.
 *
 * **Er folgt der eingestellten Sprache**, nicht der des Browsers: Dieselbe
 * Regel wie bei {@link periodenname} und {@link betrag}. Ein Datum im
 * US-Format neben deutschen Beträgen wäre zwei Zahlensprachen auf einem
 * Bildschirm.
 */
export function tagesdatum(iso: string | null | undefined): string {
  if (!iso) {
    return '—'
  }

  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(gebietsschema()).format(d)
}

/**
 * Ein Zeitpunkt mit Uhrzeit — „27.09.2026, 14:32:05" (Auftrag vom
 * 2026-09-27).
 *
 * **Warum hier die Sekunde zählt und bei {@link tagesdatum} nicht:** Ein
 * Stammdatum wird selten geändert, und die Frage lautet „wann zuletzt". Ein
 * Konzernabschluß wird an einem Abschlußtag mehrfach gerechnet, und die Frage
 * lautet „vor oder nach der Nachlieferung". Dafür ist die Uhrzeit die
 * Auskunft und das Datum allein keine.
 */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) {
    return '—'
  }

  const d = new Date(iso)

  return Number.isNaN(d.getTime())
    ? iso
    : new Intl.DateTimeFormat(gebietsschema(), {
        dateStyle: 'short',
        timeStyle: 'medium',
      }).format(d)
}

/**
 * Ein Textbaustein des Rechenkerns wird zum Satz.
 *
 * Die Werte werden nach ihrer Art gesetzt: Ein Betrag bekommt die
 * Zifferngruppierung des Anwenders, eine Periode ihren ausgeschriebenen Monat,
 * alles andere bleibt, wie es kam. Genau deshalb steht im Drill-down nicht
 * mehr „40000.00", während zwei Zentimeter weiter „40.000,00" in der Spalte
 * steht.
 *
 * **Ein unbekannter Schlüssel bekommt einen Satz, keinen Schlüssel.** Er kommt
 * vom Server und lässt sich nicht wie der Oberflächenkatalog beim Übersetzen
 * prüfen; auf dem Bildschirm bliebe er technischer Text und verstieße gegen
 * R145. Der Hinweis geht deshalb an die Entwicklungskonsole, nicht an den
 * Fachanwender.
 */
export function satz(
  baustein: Kerntext | null | undefined,

  /**
   * Wie ein Kontoschlüssel zu einer Bezeichnung wird.
   *
   * **Normalerweise der Katalog der Anlage** — und genau deshalb steht das
   * hier: Die Prüferansicht rechnet einen erfundenen Lehrkonzern vor, dessen
   * Konten der Katalog nicht kennt (R807). Sie reicht ihren eigenen
   * Namensgeber herein, statt eine zweite Fassung dieser Funktion zu bekommen.
   */
  kontoname: (schluessel: string) => string = kontobezeichnung,
  /** Mit der Verweismarke, für {@link Verweissatz} (R1527) — siehe {@link t}. */
  mitVerweis = false,
): string {
  if (!baustein) {
    return ''
  }

  const vorhanden = Object.hasOwn(de, baustein.schluessel)
  if (!vorhanden) {
    // eslint-disable-next-line no-console
    console.warn(`Kein Text zum Schlüssel "${baustein.schluessel}".`)
    return t('text.unbekannt')
  }

  const werte: Record<string, string> = {}
  for (const w of baustein.werte) {
    // **Ein Kurs steht im Satz mit vier Stellen** (R1083, Entscheidung vom
    // 2026-10-05): „0,8777" liest sich, „0,8776549061" nicht. Der genaue Wert
    // steht für den Prüfer in der Spalte Kurs desselben Fensters
    // (`kursangabe`). Erkannt am Feld, wie Anteil und Anzahl.
    if (/^kurs[A-Z]/.test(w.feld) && Number.isFinite(Number(w.wert))) {
      werte[w.feld] = Number(w.wert).toLocaleString(zahlenschema(), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })
      continue
    }

    // **Eine Anzahl steht wie ein Betrag** (R841, 7.2 Abschnitt 23): „Alle
    // 1.342 Einträge" wie die Kachel darunter. Der Dienst sagt es mit der Art
    // `anzahl`; was der Rechenkern schon gespeichert hat, trägt noch `text`
    // oder `zahl` und wird am Feld erkannt — dieselbe Haltung wie beim Anteil.
    if ((w.art === 'anzahl' || istAnzahlfeld(w.feld)) && Number.isFinite(Number(w.wert))) {
      werte[w.feld] = anzahlwort(Number(w.wert))
      continue
    }

    werte[w.feld] =
      w.art === 'zahl'
        ? // **Ein Anteil ist kein Betrag** (R840, 7.2 Abschnitt 22.3): Der
          // Rechenkern liefert ihn in Prozent — 80, nicht 0,8 —, und so
          // steht er im Prüfcode des Laufs. Was der Rechenkern gespeichert
          // hat, bleibt; die Anzeige schreibt die Prozentzahl ohne Endnullen,
          // und der Satz sagt selbst „{quote} Prozent". Über `betrag` stand
          // dort „80,00 Prozent".
          istProzentfeld(w.feld)
          ? prozentzahl(w.wert)
          : betrag(Number(w.wert))
        : w.art === 'periode'
          ? periodenname(Number(w.wert))
          : // Ein Tag als JJJJ-MM-TT (R1385) — zur Mittagszeit gelesen, damit
            // keine Zeitzone ihn auf den Vortag schiebt.
            w.art === 'datum'
            ? tagesdatum(`${w.wert}T12:00:00`)
            : w.art === 'konto'
            ? kontoname(w.wert)
            : // **Ein Schlüssel der Sprachdatei wird nachgeschlagen** — der
              // Rechenkern kennt keine Sprache und gibt deshalb den Schlüssel
              // weiter. Kennt die Sprachdatei ihn nicht, steht er unverändert
              // da: Ein halber Satz mit „unbekannt" darin sagt weniger als der
              // Schlüssel selbst.
              w.art === 'katalog'
              ? Object.hasOwn(de, w.wert)
                ? t(w.wert as Sprachschluessel)
                : w.wert
              : w.wert
  }

  return t(baustein.schluessel as Sprachschluessel, werte, mitVerweis)
}

/**
 * Der Regelname einer Buchung.
 *
 * Der Code kommt aus dem Hash und ist sprachneutral. Ein Code, den der Katalog
 * nicht kennt, wird **unverändert ausgegeben** — anders als bei einem Satz:
 * Er ist kurz, er steht in einer Spalte neben der Buchung, und er ist für den
 * Prüfer die Verbindung zum Nachweis. Ihn durch „unbekannt" zu ersetzen wäre
 * hier der größere Schaden.
 */
export function regelname(code: string): string {
  const schluessel = `regel.${code}`
  return Object.hasOwn(de, schluessel) ? t(schluessel as Sprachschluessel) : code
}

/** Die Beschriftung eines Herleitungsfeldes. */
export function herleitungsname(feld: string): string {
  return Object.hasOwn(de, feld) ? t(feld as Sprachschluessel) : feld
}

/**
 * Ein Text zu einem Schlüssel, den es geben kann oder nicht.
 *
 * **Für Vorräte, die von der Schnittstelle kommen** — die Wertevorräte der
 * Stammdatenfelder etwa (7.4). Kennt die API einen Wert, den die Sprachdatei
 * noch nicht beschriftet, steht er unbeschriftet da: Das fällt beim Übersetzen
 * auf und ist ehrlicher, als ihn zu verschweigen. Für den festen
 * Oberflächenvorrat gilt das nicht — dort prüft der Übersetzer mit, und
 * `t()` ist der richtige Weg.
 */
export function textOderRoh(schluessel: string, roh: string): string {
  return Object.hasOwn(de, schluessel) ? t(schluessel as Sprachschluessel) : roh
}

/** Ob es zu einem Schlüssel einen Text gibt — etwa die Einzahl eines Befunds (R1527). */
export function gibtText(schluessel: string): boolean {
  return Object.hasOwn(de, schluessel)
}

/**
 * Wie ein Feld heißt — **je Objektart, wo das Werk es dort anders nennt**,
 * sonst für alle (R1448). An der Gesellschaft heißt *Stillgelegt ab* „Inaktiv
 * seit"; an jeder anderen Objektart bleibt es, was es ist.
 */
export function feldname(objektart: string | null | undefined, feld: string): string {
  const eigen = objektart ? textOderRoh(`stammdaten.feld.${objektart}.${feld}`, '') : ''
  return eigen || t(`stammdaten.feld.${feld}` as Sprachschluessel)
}

/** Was von der Schnittstelle kommt: Schlüssel und Werte, kein Satz. */
export type Kerntext = {
  schluessel: string
  werte: { feld: string; wert: string; art: 'text' | 'zahl' | 'periode' | 'konto' | 'katalog' | 'anzahl' | 'datum' }[]
}

/* --------------------------------------------------------------------------
   Der Stammdatenkatalog
   --------------------------------------------------------------------------

   Der Rechenkern schickt in seinen Textbausteinen den *Schlüssel* eines
   Kontos — er kennt keine Sprache und dürfte deshalb keine wählen. Beschriftet
   wird hier, wo die Sprache bekannt ist.

   Der Katalog liegt im Modul und nicht im Zustand eines Bauteils: Er wird an
   sehr vielen Stellen gebraucht, ändert sich innerhalb einer Sitzung praktisch
   nie, und ihn durch den ganzen Baum zu reichen hieße, jede Zwischenstufe von
   ihm wissen zu lassen. */

type Katalogeintrag = {
  schluessel: string
  code: string
  namen: Record<string, string>
  kuerzel?: Record<string, string> | null
  /** Was eine Bewegungsart vorträgt, sonst leer (R1060). */
  vortrag?: string | null
  /** Ab welcher Periode es nicht mehr gilt — die Auswahl blendet es aus (R1447). */
  stillgelegtAb?: number | null
}

let konten = new Map<string, Katalogeintrag>()
let einheiten = new Map<string, Katalogeintrag>()
let bewegungsarten = new Map<string, Katalogeintrag>()
let ledger = new Map<string, Katalogeintrag>()
let positionen = new Map<string, Katalogeintrag>()
let kreise = new Map<string, Katalogeintrag>()
let datenquellen = new Map<string, Katalogeintrag>()
let anwender = new Map<string, Katalogeintrag>()

/**
 * Die Partnercodes (2.4 Abschnitt 10, R490) — Beschriftung UND Reihenfolge.
 *
 * **Sie stehen neben den Gesellschaften und nicht in ihnen.** Ein Partner war bis
 * zum 2026-09-13 eine Gesellschaft und hiess wie sie; seither ist er ein
 * eigenes Stammdatum, das auf eine Gesellschaft zeigen kann und nicht muss.
 * Wo hier nichts steht, gilt weiterhin die Gesellschaft — ein Bestand ohne
 * gepflegte Partnercodes bleibt lesbar.
 */
let partner = new Map<string, Katalogeintrag & { rang: number }>()

/**
 * Den Stammdatenkatalog setzen — einmal je Sitzung, in allen Sprachen.
 *
 * **Befund vom 2026-09-29 (R838):** Der Server lieferte Bewertungsebenen,
 * Positionen und Kreise seit jeher mit, und diese Stelle warf sie weg — sie
 * behielt nur Konten, Gesellschaften und Bewegungsarten. Wer eine
 * Bewertungsebene zeigen wollte, fand deshalb keinen Namensgeber und schrieb
 * „LDG-HGB" hin. Seither bleibt alles, was der Katalog trägt — auch
 * Datenquellen und Anwender, die er seit demselben Tag mitbringt.
 */
export function setzeKatalog(eintraege: {
  konten: Katalogeintrag[]
  einheiten?: Katalogeintrag[]
  bewegungsarten?: Katalogeintrag[]
  ledger?: Katalogeintrag[]
  positionen?: Katalogeintrag[]
  kreise?: Katalogeintrag[]
  datenquellen?: Katalogeintrag[]
  anwender?: Katalogeintrag[]
  nurCoko?: string[]
  statistik?: string[]
}): void {
  konten = alsNachschlagewerk(eintraege.konten)
  einheiten = alsNachschlagewerk(eintraege.einheiten)
  bewegungsarten = alsNachschlagewerk(eintraege.bewegungsarten)
  ledger = alsNachschlagewerk(eintraege.ledger)
  positionen = alsNachschlagewerk(eintraege.positionen)
  kreise = alsNachschlagewerk(eintraege.kreise)
  datenquellen = alsNachschlagewerk(eintraege.datenquellen)
  anwender = alsNachschlagewerk(eintraege.anwender)
  nurCoko = new Set(eintraege.nurCoko ?? [])
  statistikkonten = new Set(eintraege.statistik ?? [])
  katalogzustand = 'da'
}

/**
 * **Die Konten, auf die nur CoKo bucht**, wie der Katalog sie nennt (R1427,
 * R1656): die technischen und jedes eigene Konto einer
 * Stichtagskursanpassung. `Festkonten.ts` nimmt sie aus jeder Auswahl.
 */
let nurCoko = new Set<string>()

/** Ob nur CoKo auf ein Konto bucht — laut Katalog (R1656). */
export function istNurCokoImKatalog(schluessel: string): boolean {
  return nurCoko.has(schluessel)
}

/**
 * **Die Statistikkonten**, wie der Katalog sie nennt (2.1 Abschnitt 3.6,
 * R1734). `Festkonten.ts` nimmt sie aus jeder Auswahl außer der Buchungsmaske.
 */
let statistikkonten = new Set<string>()

/** Ob ein Konto ein Statistikkonto ist — laut Katalog (R1734). */
export function istStatistikkontoImKatalog(schluessel: string): boolean {
  return statistikkonten.has(schluessel)
}

/** Ein eben angelegtes Statistikkonto, bis der Katalog es selbst nennt (R1734). */
export function merkeStatistikkonto(schluessel: string): void {
  statistikkonten.add(schluessel)
}

function alsNachschlagewerk(eintraege: Katalogeintrag[] | undefined): Map<string, Katalogeintrag> {
  return new Map((eintraege ?? []).map((e) => [e.schluessel, e]))
}

/**
 * **Ob das Namensverzeichnis schon da ist** (7.2 Abschnitt 20, R1661).
 *
 * Es kommt beim Start im Hintergrund (R1007). Bis zum 2026-10-12 gab jeder
 * Namensgeber bis dahin den Code zurück — auf der Seite *Konzernabschlüsse*
 * sah der Wächter so *„KRS-000016-18“* statt *„Konzern Holding AG“*. Jetzt
 * steht dort „…“, bis das Verzeichnis da ist; kommt es nicht, der Code.
 *
 * **Ohne Start steht es auf „da“** — ein Prüffall, der keinen Katalog setzt,
 * bekommt weiter den Code und nicht die Auslassungspunkte.
 */
let katalogzustand: 'da' | 'laedt' | 'fehlt' = 'da'

/** Der Start fragt das Verzeichnis an: Bis es da ist, steht an jedem Namen „…“. */
export function katalogLaedt(): void {
  katalogzustand = 'laedt'
}

/** Das Verzeichnis kam nicht (R1007): Ein Name ohne Eintrag zeigt wieder den Code. */
export function katalogFehlt(): void {
  katalogzustand = 'fehlt'
}

/** Was ein Name ohne Eintrag zeigt — „…“, solange das Verzeichnis lädt, sonst den Code (R1661, R792). */
function ohneEintrag(schluessel: string): string {
  return katalogzustand === 'laedt' ? '…' : schluessel
}

/** Die Nachschlagewerke für den Aufriss — Konten und Gesellschaften. */
export function katalog(): {
  konten: Map<string, Katalogeintrag>
  einheiten: Map<string, Katalogeintrag>
  partner: Map<string, Katalogeintrag & { rang: number }>
} {
  return { konten, einheiten, partner }
}

/**
 * Die Partnercodes setzen — geholt wie der Katalog, einmal je Sitzung.
 *
 * Sie kommen aus der gewoehnlichen Stammdatenliste und nicht aus einem
 * eigenen Satz: Dieselbe Auskunft zweimal auszuliefern hiesse, sie zweimal zu
 * pflegen.
 */
export function setzePartnercodes(
  eintraege: { schluessel: string; code: string; namen: Record<string, string>; rang: number }[],
): void {
  partner = new Map(eintraege.map((e) => [e.schluessel, e]))
}

/**
 * Der Name einer Bewegungsart in der gewählten Sprache.
 *
 * Der Wertenachweis gruppiert danach (R206), und „BWA-AFA" ist keine Auskunft.
 * Wie beim Konto gilt: **Unbekanntes wird unverändert ausgegeben** — die
 * Kennung ist dann das Einzige, was es gibt.
 */
export function bewegungsartname(schluessel: string): string {
  return nameOderKennung(bewegungsarten, schluessel)
}

/**
 * Was eine Bewegungsart **heute** heißt — Name und Kürzel aus dem Katalog der
 * Sitzung, `undefined`, wo sie dort fehlt.
 *
 * Der Spiegel nimmt daraus seine Spaltenköpfe (2.5 Abschnitt 14, R1014): Ein
 * Lauf friert die Zahlen ein, nicht die Beschriftung. Am 2026-10-04 stand über
 * einer Spalte noch „Kreisänderung Zugang", obwohl am Abend davor das Kürzel
 * „KK+" erfasst war — gerechnet war zuletzt davor.
 */
export function bewegungsarteintrag(schluessel: string): Katalogeintrag | undefined {
  return bewegungsarten.get(schluessel)
}

/**
 * Was eine Bewegungsart vorträgt — oder `null`, wenn sie kein Vortrag ist
 * (2.5 Abschnitt 15, R1060).
 *
 * **Die eine Stelle der Oberfläche, die das weiß.** *Startwert der Periode*,
 * *Kumulierte Abschreibungen zum Jahresanfang* und *Übertrag Jahresergebnis
 * der Vormonate* tragen den Stand des Vormonats weiter; für den Anwender sind
 * sie keine Bewegungsart. Keine Auswahl bietet sie an (R1061), kein Beleg zeigt
 * ihre Zeile (R1063), und der Spiegel benennt ihre Spalte aus der Sprachdatei
 * (R1062).
 */
export function vortragsart(schluessel: string | null | undefined): string | null {
  return schluessel ? (bewegungsarten.get(schluessel)?.vortrag ?? null) : null
}

/** Ob eine Bewegungsart ein Vortrag ist (R1060). */
export function istVortrag(schluessel: string | null | undefined): boolean {
  return vortragsart(schluessel) !== null
}

/**
 * Eine Auswahl ohne Vortrag (R1061).
 *
 * **Was schon gewählt ist, bleibt stehen:** Eine Zuordnung, die einen Vortrag
 * trägt, zeigt ihn weiter an; nur neu wählen läßt er sich nicht.
 */
export function ohneVortrag<T extends { schluessel: string }>(eintraege: T[], gewaehlt?: string | null): T[] {
  return eintraege.filter((e) => !istVortrag(e.schluessel) || e.schluessel === gewaehlt)
}

/**
 * Die Zeilen eines Belegs ohne die Vortragszeilen (R1063).
 *
 * Eine Saldenlieferung bucht neben jeder Veränderung den Stand des Vormonats;
 * diese Zeilen heben sich im Beleg gegenseitig auf und sind keine Buchung,
 * die jemand geliefert hätte. Im Datenbestand bleiben sie.
 */
export function ohneVortragszeilen<T extends { bewegungsart?: string | null }>(zeilen: T[]): T[] {
  return zeilen.filter((z) => !istVortrag(z.bewegungsart))
}

/** Der Name einer Vortragsspalte im Spiegel — aus der Sprachdatei, nicht aus dem Katalog (R1062). */
export function vortragsname(art: string): string {
  const schluessel = `spiegel.vortrag.${art}`
  return schluessel in de ? t(schluessel as Sprachschluessel) : t('spiegel.vortrag.anfangsbestand')
}

/**
 * Der Name einer Gesellschaft in der gewählten Sprache.
 *
 * **Er hat bis zum 2026-09-26 gefehlt — und das hat dreimal denselben Fehler
 * erzeugt.** Für Konten gab es `kontobezeichnung`, für Bewegungsarten
 * `bewegungsartname`; wer eine Gesellschaft zeigen wollte, schrieb den
 * Schlüssel hin. `EH-000005-57` stand nacheinander in der Tafel der
 * Konsolidierungskreise, in der aufgelösten Verweisspalte und im Nachweis
 * eines Konsolidierungsprozesses.
 *
 * **Eine unbekannte Gesellschaft wird unverändert ausgegeben** — wie beim
 * Konto: Dort ist die Kennung das Einzige, was es gibt.
 */
export function gesellschaftsname(schluessel: string): string {
  return nameOderKennung(einheiten, schluessel)
}

/**
 * Nummer und Bezeichnung eines Kontos in der gewählten Sprache.
 *
 * **Ein unbekanntes Konto wird unverändert ausgegeben.** Es kommt vor: ein
 * Saldo auf einem Konto, das im Konzernkontenrahmen fehlt, ist genau der Fall,
 * den `hinweis.konto-unbekannt` meldet. Dort ist die Kennung das Einzige, was
 * es gibt — sie zu unterschlagen nähme dem Anwender den Handgriff.
 */
export function kontobezeichnung(schluessel: string): string {
  const eintrag = konten.get(schluessel)
  if (!eintrag) {
    return ohneEintrag(schluessel)
  }

  // **Seit R678 steht die Nummer in der Bezeichnung**, und der Katalog liefert
  // den Code leer — davor stand dann ein Leerzeichen: „ 2200 Gewinnrücklagen"
  // (beim Durchgang am 2026-10-03 in der Kontoauswahl der Datenquelle gesehen).
  const name = beschriftung(eintrag.namen)
  return [eintrag.code, name].filter((teil) => teil).join(' ') || schluessel
}

/**
 * Die Kette aus R792, an einer Stelle für alle Sorten (7.2 Abschnitt 20,
 * R838): Bezeichnung in der gewählten Sprache, sonst Code, sonst Schlüssel —
 * nie leer.
 *
 * **Ein leerer Code ist kein Code.** Der Rechenkern schickt `''`, wo die
 * Codespalte gefallen ist (R705), und `??` griffe dort nicht, weil `''`
 * kein `null` ist. Deshalb `||` und der Blick auf den Leertext.
 */
function nameOderKennung(eintraege: Map<string, Katalogeintrag>, schluessel: string): string {
  const eintrag = eintraege.get(schluessel)
  if (!eintrag) {
    return ohneEintrag(schluessel)
  }

  return beschriftung(eintrag.namen) || (eintrag.code ?? '').trim() || schluessel
}

/** Der Name einer Bewertungsebene — „LDG-HGB" ist keine Auskunft (R838). */
export function bewertungsebenenname(schluessel: string): string {
  return nameOderKennung(ledger, schluessel)
}

/** Der Name eines Konsolidierungskreises (R838). */
export function kreisname(schluessel: string): string {
  return nameOderKennung(kreise, schluessel)
}

/**
 * Die Bezeichnung einer Position (R838).
 *
 * **Nur die Bezeichnung:** Positionen tragen ihre Nummer im Namen — „B.II.2
 * Forderungen gegen verbundene Unternehmen" — und keinen Code daneben, anders
 * als ein Konto.
 */
export function positionsbezeichnung(schluessel: string): string {
  return nameOderKennung(positionen, schluessel)
}

/** Der Name einer Datenquelle — wer geliefert hat (R838). */
export function datenquellenname(schluessel: string): string {
  return nameOderKennung(datenquellen, schluessel)
}

/**
 * Der Name eines Anwenders (R838, R792).
 *
 * **Maschinenkennungen heißen beim Namen ihrer Herkunft:** Was die
 * Einrichtung des Hauses geschrieben hat, trägt `system` oder `migration…`;
 * was der Datenimport gebucht hat, `import`. Ein Finanzer liest
 * *Einrichtung* und *Datenimport*, nicht die Kennung eines Programms.
 *
 * **Ein Anwender, den es nicht mehr gibt, behält seine Kennung** (R792): Er
 * hat gerechnet, und sein Lauf trägt weiter seinen Schlüssel. Das ist dann
 * das Einzige, was es gibt — leer wäre der größere Schaden.
 */
export function anwendername(schluessel: string): string {
  const kennung = schluessel.trim()
  const klein = kennung.toLowerCase()

  if (klein === '' || klein === 'unbekannt') {
    return t('anwender.unbekannt')
  }
  if (klein === 'system' || klein.startsWith('migration')) {
    return t('anwender.einrichtung')
  }
  if (klein === 'import') {
    return t('anwender.datenimport')
  }

  return nameOderKennung(anwender, kennung)
}

/** Die Kennungsmuster des Hauses — Präfix, Strich, dann Ziffern, Buchstaben und Striche. */
const KENNUNGSMUSTER = /\b(EH|LDG|KRS|KTO|POS|BWA|DQ|ANW)-[A-Za-z0-9][A-Za-z0-9-]*/g

/** Je Präfix der eine Namensgeber seiner Sorte (R838). */
const NAMENSGEBER: Record<string, (schluessel: string) => string> = {
  EH: gesellschaftsname,
  LDG: bewertungsebenenname,
  KRS: kreisname,
  KTO: kontobezeichnung,
  POS: positionsbezeichnung,
  BWA: bewegungsartname,
  DQ: datenquellenname,
  ANW: anwendername,
}

/**
 * Ob eine Kennung im Katalog einen Namen hat (7.2 Abschnitt 38, R1223).
 *
 * `null`, wenn ihre Sorte gar keinen Katalog hat — ein Konsolidierungsschritt
 * `KSS-3` etwa: Dort ist der Name Sache des Bildschirms, nicht der
 * Stammdaten. Sonst, ob ihr Namensgeber etwas anderes zurückgibt als die
 * Kennung selbst — eine Bezeichnung oder wenigstens einen Code.
 */
export function hatNamen(kennung: string): boolean | null {
  const geber = NAMENSGEBER[kennung.slice(0, kennung.indexOf('-'))]
  return geber ? geber(kennung) !== kennung : null
}

/**
 * Ein gespeicherter Text mit Namen gelesen (7.2 Abschnitt 20, R838 —
 * Entscheidung 2 des Auftraggebers vom 2026-09-29).
 *
 * *„Saldenliste EH-000005-57 202612: 40 Zeilen in EUR"* ist ein beim Import
 * gespeicherter Belegname, und ein Lieferbeleg ist unveränderlich — er ist
 * die Nachweisspur (R388). Also wird nicht der Beleg umgeschrieben, sondern
 * die Anzeige liest ihn mit Namen: Jeder bekannte Schlüssel im Text wird
 * durch seine Bezeichnung ersetzt; der gespeicherte Text bleibt, wie er ist,
 * für alte wie für neue Belege. **Ein unbekannter Schlüssel bleibt stehen:**
 * Sein Namensgeber gibt ihn unverändert zurück.
 */
export function mitNamen(text: string): string {
  return text.replace(KENNUNGSMUSTER, (kennung: string, praefix: string) =>
    (NAMENSGEBER[praefix] ?? ((k: string) => k))(kennung),
  )
}

/**
 * Eine Periode im gespeicherten Text als `MM/JJJJ` — *„Saldenliste … 12/2026"*.
 *
 * **Eigenständig, nicht Teil von etwas Längerem:** kein Datum (`31/12/2026`),
 * kein Aktenzeichen (`12/2026-4`), kein Stück einer Kennung.
 */
const PERIODE_MONAT_JAHR = /(?<![\w/.,-])(0[1-9]|1[0-2])\/((?:19|20)\d{2})(?![\w/-]|[.,]\d)/g

/**
 * Die sechsstellige Speicherform `JJJJMM` als eigenständige Zahl — *„… 202612:
 * 40 Zeilen in EUR."*
 *
 * **Nicht Teil einer längeren Ziffernfolge, nicht Teil einer Belegnummer**
 * (`2026-000478`, `202612-0004`), **nicht Teil einer Kennung**
 * (`EH-202612`) **und nicht Teil eines Betrags** (`202612,50`,
 * `1.202612`). Ein Punkt oder Doppelpunkt am Satzende dahinter stört nicht.
 */
const PERIODE_SPEICHERFORM = /(?<![\w.,-])((?:19|20)\d{2})(0[1-9]|1[0-2])(?![\w-]|[.,]\d)/g

/**
 * Ein gespeicherter Belegtext für die Anzeige gelesen — mit Namen **und** mit
 * Monat und Jahr (7.2 Abschnitt 21, R839 — Eine Periode liest sich als Monat
 * und Jahr; Entscheidung 3 des Auftraggebers vom 2026-09-30).
 *
 * Im Buchungsjournal stand dieselbe Periode in einer Zeile dreimal
 * verschieden: im Belegnamen *„Saldenliste Holding AG 12/2026"*, in der
 * Erläuterung *„… 202612: 40 Zeilen in EUR."*, in der Spalte Periode
 * *„Dezember 2026"*. **Ein Lieferbeleg ist unveränderlich** (R388) — also
 * wird, wie bei den Kennungen (R838, `mitNamen`), nicht der Beleg
 * umgeschrieben, sondern die Anzeige liest ihn: am Bildschirm steht
 * *„Saldenliste Holding AG Dezember 2026"*, der Beleg bleibt, wie er ist.
 *
 * **Der Preis ist benannt:** Erkannt wird am Muster. Eine sechsstellige Zahl,
 * die zufällig wie eine Periode aussieht — *„Auftrag 202612"* —, wird
 * mitgelesen. Deshalb **nur für Belegname, Erläuterung und Buchungstext** und
 * nicht für Dateinamen: Dort ist eine Ziffernfolge ein Teil des Namens.
 *
 * **Erst die Perioden im gespeicherten Text, dann die Namen.** Umgekehrt würde
 * ein eingesetzter Name nachträglich zerlegt — eine Gesellschaft *„Projekt
 * 202612 GmbH"* hieße am Bildschirm *„Projekt Dezember 2026 GmbH"*. Eine
 * Kennung nimmt dabei keinen Schaden: Ihre Ziffern stehen hinter einem Strich
 * oder einem Buchstaben, und dort wird keine Periode gelesen. Eine
 * Kontonummer wie *„0210 Grundstücke"* ist vierstellig und bleibt, was sie
 * ist.
 *
 * Neue Meldungen des Programms brauchen das nicht: Sie tragen die Periode als
 * Periode (`Textbaustein.Periode`), und `satz` schreibt sie aus.
 */
export function belegtext(text: string): string {
  const mitPerioden = text
    .replace(PERIODE_MONAT_JAHR, (_roh: string, monat: string, jahr: string) =>
      periodenname(Number(jahr) * 100 + Number(monat)),
    )
    .replace(PERIODE_SPEICHERFORM, (_roh: string, jahr: string, monat: string) =>
      periodenname(Number(jahr) * 100 + Number(monat)),
    )

  // Ein gespeicherter Text verliert seine Regelnummern erst in der Anzeige
  // (7.2 Abschnitt 34, R1133) — der Beleg bleibt, wie er ist (R388).
  return ohneRegelvermerk(mitNamen(mitBetraegen(mitPerioden)))
}

/**
 * Ein Buchungstext, wie der Wertenachweis ihn zeigt (R395, R1083).
 *
 * **Zwei Herkünfte, eine Anzeige:** Was ein Mensch geliefert oder erfasst hat,
 * steht als Text da und wird mit Namen und Monat gelesen. Was der Rechenkern
 * geschrieben hat — der Satz der Umrechnung mit ihren Kursen —, steht als
 * Textbaustein da und wird in der Sprache des Anwenders gesetzt.
 */
export function buchungstext(text: string | null | undefined): string | null {
  if (!text) return null
  const baustein = alsTextbaustein(text)
  if (baustein) return satz(baustein)
  // **Was der Datenimport selbst hineinschreibt, heißt „Datenimport"** (7.2
  // Abschnitt 34, R1134 — Entscheidung vom 2026-10-06), auch bei Zeilen, die
  // vorher gespeichert wurden. Ein Text, den die Lieferdatei mitbringt, hat ein
  // Mensch geschrieben und bleibt.
  if (IMPORTTEXT.test(text)) return t('nachweis.buchungstext.datenimport')
  return text.startsWith('{"schluessel"') ? text : belegtext(text)
}

/**
 * Die Buchungstexte, die der Datenimport selbst schreibt (`Importdienst`):
 * Verkehrszahl, Eröffnung, Zugang zum Konsolidierungskreis, Nachbuchung,
 * Ergebnis vor dem Eintritt, Übertrag des Jahresergebnisses, Jahreswechsel und
 * der angenommene Vorschlag der Datenquelle. Bis zum 2026-10-06 stand im
 * Wertenachweis etwa *„Verkehrszahl: geliefert 1.200,00 minus Stand 1.000,00
 * (R539)"*.
 */
const IMPORTTEXT =
  /^(?:Verkehrszahl: geliefert |Eröffnung(?: als Zugang zum Konsolidierungskreis)?: |Zugang zum Konsolidierungskreis: erste Lieferung|Nachbuchung für \d{6}: |Ergebnis vor dem Eintritt: |Jahresergebnis der Vormonate als Zugang|Übertrag des Jahresergebnisses der Vormonate|Jahreswechsel: )|— Vorschlag der Datenquelle, angenommen \(R874\)$/

/** Ein gespeicherter Textbaustein des Rechenkerns — oder `null`, wenn der Text keiner ist. */
function alsTextbaustein(text: string | null | undefined): Kerntext | null {
  if (!text?.startsWith('{"schluessel"')) return null
  try {
    return JSON.parse(text) as Kerntext
  } catch {
    return null
  }
}

/**
 * Die genauen Kurse einer Umrechnungszeile für die Spalte Kurs — oder
 * `null` (R1083, Entscheidung vom 2026-10-05).
 *
 * Der Satz zeigt die Kurse mit vier Stellen; **der Prüfer braucht sie genau**,
 * und zwar im selben Fenster. Eine Rechenbuchung trägt keinen eigenen Kurs —
 * sie hat keinen Betrag in Landeswährung —, also stehen hier die beiden, mit
 * denen sie gerechnet ist: *„historisch 0,8776549061 · Stichtag 0,8707009142"*.
 */
export function kursangabe(text: string | null | undefined): string | null {
  const baustein = alsTextbaustein(text)
  const wert = (feld: string) => baustein?.werte.find((w) => w.feld === feld)?.wert
  const historisch = Number(wert('kursHistorisch'))
  const stichtag = Number(wert('kursStichtag'))
  if (!baustein || !wert('kursHistorisch') || !wert('kursStichtag') || !Number.isFinite(historisch) || !Number.isFinite(stichtag)) {
    return null
  }
  return `${t('nachweis.kurs.historisch')} ${kurswert(historisch)} · ${t('nachweis.kurs.stichtag')} ${kurswert(stichtag)}`
}

/**
 * Die Erläuterung eines Belegs unter seinem Namen — **ohne die Zeilenzahl**,
 * die der Datenimport beim Buchen hineinschreibt (2.5 Abschnitt 15, R1063).
 *
 * *„Saldenliste Holding AG Dezember 2026: 40 Zeilen in EUR."* zählte die
 * Vortragszeilen mit, die der Beleg nicht zeigt, und die Zahl beantwortet
 * keine Frage des Anwenders (Auftrag vom 2026-10-05). Gespeichert bleibt der
 * Text, wie er ist (R838). Was danach nur noch den Namen wiederholt, entfällt
 * ganz: `null`.
 */
export function belegerlaeuterung(erlaeuterung: string | null | undefined, name?: string | null): string | null {
  if (!erlaeuterung) return null
  // **Nennt sie nur Gesellschaft und Monat, entfällt sie ganz** (Auftrag vom
  // 2026-10-05): Der Datenimport schreibt „Saldenliste … 202612: 4 Zeilen in
  // EUR." in jeden Beleg, auch unter einen Anlagenspiegel — dort stand dann
  // „Saldenliste" unter „Anlagenspiegel". Beides steht schon im Namen — also
  // nur, wo es einen Namen gibt.
  if (name?.trim() && /^Saldenliste \S+ \d{6}: \d+ Zeilen in [A-Z]{3}\.$/.test(erlaeuterung.trim())) return null
  const ohneZahl = belegtext(erlaeuterung)
    .replace(/\s*[:—–]\s*\d+\s+(?:Zeilen|Verkehrszahlen)\s+in\s+[A-Z]{3}(?=\s*(?:\(|\.|$))/g, '')
    .trim()
  const kern = (s: string) => s.replace(/[.\s]+$/, '')
  if (!kern(ohneZahl) || (name && kern(ohneZahl) === kern(belegtext(name)))) return null
  return ohneZahl
}

/**
 * Ein Betrag, wie der Rechenkern ihn in eine Meldung schreibt (R876):
 * deutsch, mit Tausenderpunkt und **genau zwei Ziffern nach dem Komma** —
 * *„-353.291,70"*. Das Komma mit zwei Ziffern danach unterscheidet ihn von
 * einem Abschnittsverweis (*„3.35"*), einem Datum (*„01.01."*) und einer
 * Aufzählung (*„R866, R867"*).
 */
//
// **Dazu die alte Schreibweise, die bis zum 2026-10-03 gespeichert wurde**
// (R877): englisch **mit Tausendergruppe** — *„4,788,000.00"* — und die Null
// *„0.00"*. Ein kleiner Betrag ohne Gruppe (*„27.50"*) ist von *„Abschnitt
// 3.35"* nicht zu unterscheiden und bleibt, wie er ist; das ist der benannte
// Preis (7.2 Abschnitt 25).
//
// **Beides in einem Durchgang:** Nacheinander angewandt, läse der zweite den
// Betrag, den der erste schon ins Zahlenformat des Anwenders gesetzt hat —
// im französischen *„4 788 000,00"* stünde *„000,00"* noch einmal zur Wahl.
const BETRAG_IM_TEXT =
  /(?<![\w.,])(-?)(?:(\d{1,3}(?:\.\d{3})*),(\d{2})|(\d{1,3}(?:,\d{3})+|0)\.(\d{2}))(?![\d,]|\.\d)/g

/**
 * Die Beträge einer gespeicherten Meldung im Zahlenformat des Anwenders
 * (7.2 Abschnitt 25, R877 — wie im Bericht, R840).
 */
function mitBetraegen(text: string): string {
  return text.replace(
    BETRAG_IM_TEXT,
    (_roh: string, minus: string, deutsch?: string, deutschKomma?: string, alt?: string, altKomma?: string) =>
      deutsch !== undefined
        ? betrag(Number(`${minus}${deutsch.split('.').join('')}.${deutschKomma}`))
        : betrag(Number(`${minus}${alt!.split(',').join('')}.${altKomma}`)),
  )
}

/**
 * Code und Bezeichnung eines Katalogeintrags für ein Auswahlfeld (R801).
 *
 * **Der Gedankenstrich hängt am Code und nicht am Eintrag.** Die Fassung
 * `${code} — ${name}` stand am 2026-09-26 an sieben Stellen einzeln im
 * Quelltext. Wo ein Code fehlt — die Positionen tragen ihre Nummer im Namen —
 * begann der Eintrag mit einem Gedankenstrich ins Leere: *„— B.II.2
 * Forderungen gegen verbundene Unternehmen"*.
 *
 * **Und wo beides fehlt, steht die Kennung da.** Sie zu unterschlagen ergäbe
 * einen leeren Eintrag, den niemand wählen kann.
 */
export function katalogbezeichnung(eintrag: {
  schluessel: string
  code: string
  namen: Record<string, string>
}): string {
  const name = beschriftung(eintrag.namen)
  const code = eintrag.code.trim()

  if (code && name) {
    return `${code} — ${name}`
  }

  return name || code || eintrag.schluessel
}

/**
 * Eine Beschriftung in der gewählten Sprache — oder dem Nächstbesten.
 *
 * Gestuft und nicht zufällig: die gewählte Sprache, dann Deutsch, dann
 * irgendeine vorhandene. Ein Stammdatum ohne jede Bezeichnung ist ein
 * Pflegefehler; eine leere Zeile im Bericht wäre der größere Schaden.
 */
export function beschriftung(namen: Record<string, string> | null | undefined): string {
  if (!namen) {
    return ''
  }

  return namen[aktuell] ?? namen[RUECKFALL] ?? Object.values(namen)[0] ?? ''
}

/**
 * Das Kürzel eines Stammdatums — oder das Nächstbeste (7.4, R238).
 *
 * **Die Kette ist eine andere als bei `beschriftung`, und zwar absichtlich:**
 * Kürzel in der gewählten Sprache · Kürzel auf Deutsch · *voller Name* ·
 * Rückfall. Der dritte Schritt ist der Unterschied. Ein Kürzel in irgendeiner
 * dritten Sprache wäre hier schlechter als der volle Name in der richtigen: Im
 * Kopf eines englischen Anlagenspiegels ist „Kapitalerh." keine Auskunft,
 * „Capital increase" schon — nur eben breit.
 *
 * **Maschinell gekürzt wird nie.** Eine abgeschnittene Bezeichnung sieht aus
 * wie eine gepflegte und ist keine (R145). Wer den Kopf schmal haben will,
 * pflegt ein Kürzel.
 */
export function kurzform(
  kuerzel: Record<string, string> | null | undefined,
  namen: Record<string, string> | null | undefined,
  rueckfall = '',
): string {
  const kurz = kuerzel ? (kuerzel[aktuell] ?? kuerzel[RUECKFALL]) : undefined
  return kurz || beschriftung(namen) || rueckfall
}

/** Wie viele Begriffe des Hauses gesetzt sind — für den Betriebsbildschirm. */
export function anzahlHausbegriffe(code: string = aktuell): number {
  return Object.keys(beschriftungen[code] ?? {}).length
}
